import {
  Button,
  ButtonGroup,
  buttonVariants,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Label,
  Switch,
} from '@design-system';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { FileDown, MoreHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { putClientCalculationSettingsByClientIdRequest, selectClient } from '@/model';
import { usePdfReportSections } from '../stores/pdf-report-sections-store';

export function PdfReportOptionsDropdown({ compact = false }: { compact?: boolean }) {
  const dispatch = useDispatch();
  const { clientId, clientCalculationSettings } = useSelector(selectClient)!;
  const {
    includeCareJourney,
    includePredictedCareNeeds,
    toggleCareJourney,
    togglePredictedCareNeeds,
  } = usePdfReportSections();

  const hideRoiInPdf = clientCalculationSettings?.hideReturnOnInvestmentInPdf === true;
  const [hideRoiInPdfOptimistic, setHideRoiInPdfOptimistic] = useState(hideRoiInPdf);

  useEffect(() => {
    setHideRoiInPdfOptimistic(hideRoiInPdf);
  }, [hideRoiInPdf]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            buttonVariants({
              variant: compact ? 'outline' : 'default',
              size: compact ? 'icon-sm' : 'icon',
            }),
            'rounded-l-none',
            compact ? 'border-input border-l' : 'border-primary-foreground/25 border-l',
          )}
          aria-label="PDF report options"
          data-testid="pdf-report-options-dropdown-trigger"
        >
          <MoreHorizontal className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-72 p-2"
      >
        <DropdownMenuLabel className="px-2 py-1.5">PDF report settings</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="flex cursor-default flex-row items-center justify-between gap-3 px-2 py-2"
          onSelect={e => e.preventDefault()}
        >
          <Label
            htmlFor="pdf-opt-care-journey"
            className="text-foreground flex-1 cursor-pointer text-sm font-normal"
          >
            Include Care Journey section
          </Label>
          <Switch
            id="pdf-opt-care-journey"
            checked={includeCareJourney}
            onCheckedChange={() => toggleCareJourney()}
            onPointerDown={e => e.stopPropagation()}
          />
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex cursor-default flex-row items-center justify-between gap-3 px-2 py-2"
          onSelect={e => e.preventDefault()}
        >
          <Label
            htmlFor="pdf-opt-predicted-needs"
            className="text-foreground flex-1 cursor-pointer text-sm font-normal"
          >
            Include Predicted Care Needs
          </Label>
          <Switch
            id="pdf-opt-predicted-needs"
            checked={includePredictedCareNeeds}
            onCheckedChange={() => togglePredictedCareNeeds()}
            onPointerDown={e => e.stopPropagation()}
          />
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex cursor-default flex-row items-center justify-between gap-3 px-2 py-2"
          onSelect={e => e.preventDefault()}
        >
          <Label
            htmlFor="pdf-opt-hide-roi"
            className="text-foreground flex-1 cursor-pointer text-sm font-normal"
          >
            Hide ROI completely from PDF
          </Label>
          <Switch
            id="pdf-opt-hide-roi"
            checked={hideRoiInPdfOptimistic}
            onCheckedChange={checked => {
              setHideRoiInPdfOptimistic(checked);
              dispatch(
                putClientCalculationSettingsByClientIdRequest({
                  clientId,
                  clientCalculationSettings: { hideReturnOnInvestmentInPdf: checked },
                }),
              );
            }}
            onPointerDown={e => e.stopPropagation()}
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DownloadReportWithPdfOptions({
  onDownload,
  compact = false,
  className,
  loading = false,
}: {
  onDownload: () => void;
  compact?: boolean;
  className?: string;
  loading?: boolean;
}) {
  const { flags } = useFeatureFlags();
  const showPdfOptions = flags.onboardingPdfReportOptions === true;

  if (!showPdfOptions) {
    return (
      <Button
        variant={compact ? 'outline' : 'default'}
        size={compact ? 'sm' : 'default'}
        onClick={onDownload}
        disabled={loading}
        data-testid="download-report-button"
        className={className}
      >
        {loading ? 'Sending...' : 'Download Report'}
        {!loading && <FileDown />}
      </Button>
    );
  }

  return (
    <ButtonGroup className={cn('w-full', className)}>
      <Button
        variant={compact ? 'outline' : 'default'}
        size={compact ? 'sm' : 'default'}
        onClick={onDownload}
        disabled={loading}
        data-testid="download-report-button"
        className="min-w-0 flex-1"
      >
        {loading ? 'Sending...' : 'Download Report'}
        {!loading && <FileDown />}
      </Button>
      <div className="shrink-0">
        <PdfReportOptionsDropdown compact={compact} />
      </div>
    </ButtonGroup>
  );
}
